
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Link, Save, X, Edit } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SocialMediaLink {
  id: string;
  platform: string;
  url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const AdminSocialMediaManagement = () => {
  const [links, setLinks] = useState<SocialMediaLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingLink, setEditingLink] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<SocialMediaLink>>({});

  useEffect(() => {
    fetchSocialLinks();
  }, []);

  const fetchSocialLinks = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('social_media_links')
        .select('*')
        .order('platform');

      if (error) throw error;
      setLinks(data || []);
    } catch (error) {
      console.error('Error fetching social links:', error);
      toast.error('Error loading social media links');
    } finally {
      setLoading(false);
    }
  };

  const updateLink = async (linkId: string, updates: Partial<SocialMediaLink>) => {
    try {
      const { error } = await supabase
        .from('social_media_links')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', linkId);

      if (error) throw error;
      
      toast.success('Social media link updated successfully');
      setEditingLink(null);
      setEditForm({});
      fetchSocialLinks();
    } catch (error) {
      console.error('Error updating link:', error);
      toast.error('Error updating social media link');
    }
  };

  const toggleActive = async (linkId: string, isActive: boolean) => {
    await updateLink(linkId, { is_active: isActive });
  };

  const startEditing = (link: SocialMediaLink) => {
    setEditingLink(link.id);
    setEditForm({
      url: link.url
    });
  };

  const cancelEditing = () => {
    setEditingLink(null);
    setEditForm({});
  };

  const saveEditing = async () => {
    if (editingLink && editForm.url) {
      await updateLink(editingLink, { url: editForm.url });
    }
  };

  const getPlatformIcon = (platform: string) => {
    const icons = {
      twitter: '🐦',
      instagram: '📷',
      linkedin: '💼',
      youtube: '📺'
    };
    return icons[platform as keyof typeof icons] || '🔗';
  };

  if (loading) {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Link className="w-8 h-8 mx-auto mb-4 animate-pulse text-blue-400" />
            <p className="text-white">Loading social media links...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center">
          <Link className="w-6 h-6 mr-2" />
          Social Media Management
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-gray-300">Platform</TableHead>
                <TableHead className="text-gray-300">URL</TableHead>
                <TableHead className="text-gray-300">Status</TableHead>
                <TableHead className="text-gray-300">Last Updated</TableHead>
                <TableHead className="text-gray-300">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {links.map((link) => (
                <TableRow key={link.id} className="border-white/10">
                  <TableCell>
                    <div className="flex items-center">
                      <span className="mr-2 text-lg">{getPlatformIcon(link.platform)}</span>
                      <span className="text-white font-medium capitalize">{link.platform}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {editingLink === link.id ? (
                      <Input
                        value={editForm.url || ''}
                        onChange={(e) => setEditForm({...editForm, url: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="Enter URL"
                      />
                    ) : (
                      <span className="text-white font-mono text-sm">{link.url}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={link.is_active}
                        onCheckedChange={(checked) => toggleActive(link.id, checked)}
                      />
                      <Badge className={link.is_active ? 'bg-green-600/20 text-green-300' : 'bg-gray-600/20 text-gray-300'}>
                        {link.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-white">
                      {new Date(link.updated_at).toLocaleDateString()}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {editingLink === link.id ? (
                        <>
                          <Button
                            size="sm"
                            onClick={saveEditing}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={cancelEditing}
                            className="bg-gray-600 hover:bg-gray-700"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => startEditing(link)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminSocialMediaManagement;
